/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */

import { DeviceType } from '@/api/client'
import { Event } from '@/classes/Event'
import { useAuthStore } from '@/stores/AuthStore'
import { useEventStore } from '@/stores/EventStore'
import { ref } from 'vue'
import type { TrackingType } from '@/api/client'
import { useTaskStore } from '@/stores/TaskStore'
import { Task } from '@/classes/Task'
import { Site } from '@/api/client'

export type tEmployee = {
  id: number
  first_name: string
  last_name: string
  avatar_url?: string | null
  hashed_pin: string
  pin_required: boolean
  pin_change_required: boolean
  qr_hash?: string | null
  nfc_id?: string | null
  lastEvent?: Event
  can_use_time_tracking: boolean
  can_visit_my_area: boolean
  can_request_absences: boolean
  can_show_times: boolean
  can_use_correction: boolean
  can_use_clockin_reminder?: boolean
  can_use_clockout_reminder?: boolean
  can_use_break_reminder?: boolean
  tracking_type?: TrackingType | null
  tracking_interval?: number | null
  can_see_personal_documents?: boolean
  sites?: Site[] | null
  absence_substitution_required?: boolean | null
  days_after_which_absence_substitution_is_required?: number | null
  ical_url?: string | null
  is_admin?: boolean
  can_connect_own_nfc_tag?: boolean
}

export class Employee {
  id: number
  first_name: string
  full_name_lower_case: string
  last_name: string
  avatar_url?: string | null
  hashed_pin: string
  pin_required: boolean
  pin_change_required: boolean
  qr_hash?: string | null
  nfc_id?: string | null
  lastEvent?: Event
  timeDifference?: number
  can_use_time_tracking: boolean
  can_visit_my_area: boolean
  can_request_absences: boolean
  can_show_times: boolean
  can_use_correction: boolean
  tracking_type?: TrackingType | null
  tracking_interval?: number | null
  can_see_personal_documents?: boolean
  sites?: Site[] | null
  absence_substitution_required?: boolean | null
  days_after_which_absence_substitution_is_required?: number | null
  ical_url?: string | null
  is_admin?: boolean
  can_connect_own_nfc_tag?: boolean
  can_use_clockin_reminder?: boolean
  can_use_clockout_reminder?: boolean
  can_use_break_reminder?: boolean

  constructor(employee: tEmployee) {
    this.id = employee.id
    this.first_name = Employee.capitalizeName(employee.first_name)
    this.last_name = Employee.capitalizeName(employee.last_name)
    this.full_name_lower_case = `${employee.first_name.toLowerCase()} ${employee.last_name.toLowerCase()}`
    this.avatar_url = employee.avatar_url
    this.hashed_pin = employee.hashed_pin
    this.pin_required = employee.pin_required
    this.pin_change_required = employee.pin_change_required
    this.qr_hash = employee.qr_hash
    this.nfc_id = employee.nfc_id
    this.lastEvent = employee.lastEvent
    this.can_use_time_tracking = employee.can_use_time_tracking
    this.can_visit_my_area = employee.can_visit_my_area
    this.can_request_absences = employee.can_request_absences
    this.can_show_times = employee.can_show_times
    this.can_use_correction = employee.can_use_correction
    this.tracking_type = employee.tracking_type
    this.tracking_interval = employee.tracking_interval
    this.can_see_personal_documents = employee.can_see_personal_documents
    this.sites = employee.sites
    this.absence_substitution_required = employee.absence_substitution_required
    this.days_after_which_absence_substitution_is_required =
      employee.days_after_which_absence_substitution_is_required
    this.ical_url = employee.ical_url
    this.is_admin = employee.is_admin
    this.can_connect_own_nfc_tag = employee.can_connect_own_nfc_tag
    this.can_use_clockin_reminder = employee.can_use_clockin_reminder
    this.can_use_clockout_reminder = employee.can_use_clockout_reminder
    this.can_use_break_reminder = employee.can_use_break_reminder
  }
  static capitalizeName(name: string) {
    return name
      .toLowerCase()
      .replace(
        /[A-Za-zÀ-ÖØ-öø-ÿ]+/g,
        (c) => c[0].toUpperCase() + c.substring(1),
      )
  }

  fullName() {
    return `${this.first_name} ${this.last_name}`
  }

  isClockedIn() {
    return useEventStore().employeeIsClockedIn(this.id)
  }

  initials() {
    const firstInitial =
      this.first_name && this.first_name.length > 0
        ? this.first_name[0].toUpperCase()
        : ''
    const lastInitial =
      this.last_name && this.last_name.length > 0
        ? this.last_name[0].toUpperCase()
        : ''
    return `${firstInitial} ${lastInitial}`
  }

  public canUseTimeTracking() {
    return ref(
      this.can_use_time_tracking ||
        useAuthStore().device_type === DeviceType.TERMINAL ||
        useAuthStore().device_type === DeviceType.TEAM,
    )
  }

  public canClockWorkTask(): boolean {
    return useTaskStore()
      .tasksOfEmployee(this.id)
      .reduce((acc, task) => {
        return acc || task.task_id === Task.id.WORK
      }, false)
  }

  public get canUseReminder(): boolean {
    return (
      this.can_use_clockin_reminder ||
      this.can_use_clockout_reminder ||
      this.can_use_break_reminder ||
      false
    )
  }

  public static fromServerObject(employee: tEmployee) {
    return new Employee(employee)
  }
}
